/** Dashboard widget: highest-scoring companies with a generated research report. */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/api/client";
import type { Company, ResearchReport } from "@/types";
import ScoreBadge from "@/components/research/ScoreBadge";
import WidgetCard from "./WidgetCard";

interface Pick {
  company: Company;
  report: ResearchReport;
}

export default function TopResearchPicksWidget(): React.ReactElement {
  const [picks, setPicks] = useState<Pick[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<Company[]>("/companies")
      .then(async ({ data: companies }) => {
        const results = await Promise.all(
          companies.map(async (company) => {
            try {
              const { data } = await apiClient.get<ResearchReport>(
                `/companies/${company.id}/research/latest`
              );
              return { company, report: data };
            } catch {
              return null;
            }
          })
        );
        if (cancelled) return;
        const withReports = results.filter((r): r is Pick => r !== null);
        withReports.sort((a, b) => b.report.overall_score - a.report.overall_score);
        setPicks(withReports.slice(0, 5));
      })
      .catch(() => !cancelled && setError("Unable to load research picks"));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <WidgetCard title="Top research picks" isLoading={picks === null && !error} error={error}>
      {picks && picks.length > 0 ? (
        <ul className="space-y-2">
          {picks.map(({ company, report }) => (
            <li key={company.id}>
              <Link
                to={`/research/${company.id}`}
                className="flex items-center justify-between text-sm hover:text-mist-100"
              >
                <span className="font-mono text-signal-teal">{company.symbol}</span>
                <ScoreBadge score={report.overall_score} />
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-mist-400">Generate research reports to see top picks here.</p>
      )}
    </WidgetCard>
  );
}
